import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Play, Pause, Radio } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface RadioStream {
  id: string;
  name: string;
  videoId: string;
  description: string;
  is_active?: boolean;
  last_checked_at?: string;
}


interface YouTubeRadioProps {
  volume?: number;
  onVolumeChange?: (volume: number) => void;
  onPlayingChange?: (isPlaying: boolean) => void;
  autoPlay?: boolean;
  onStreamChange?: (stream: { id: string; name: string; description: string; videoId?: string } | null) => void;
  selectedStreamId?: string;
  listenToGlobalEvents?: boolean;
}

export const YouTubeRadio = ({ volume = 50, onVolumeChange, onPlayingChange, autoPlay = false, onStreamChange, selectedStreamId, listenToGlobalEvents = true }: YouTubeRadioProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [streams, setStreams] = useState<RadioStream[]>([]);
  const [selectedStream, setSelectedStream] = useState<RadioStream | null>(null);
  const [localVolume, setLocalVolume] = useState(volume);
  const [isLoading, setIsLoading] = useState(true);
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const playerElementId = useRef<string>(`youtube-player-${Math.random().toString(36).slice(2)}`);
  const { toast } = useToast();

  // Load streams from database
  useEffect(() => {
    const loadStreams = async () => {
      const { data, error } = await (supabase as any)
        .from('youtube_streams')
        .select('*')
        .eq('is_active', true)
        .order('id');

      if (error) {
        console.error('Error loading streams:', error);
        toast({
          title: "Error loading streams",
          description: "Failed to load YouTube radio streams",
          variant: "destructive"
        });
        return;
      }

      const streamData: RadioStream[] = (data || []).map((s: any) => ({
        id: s.id,
        name: s.name,
        videoId: s.video_id,
        description: s.description,
        is_active: s.is_active,
        last_checked_at: s.last_checked_at
      }));

      setStreams(streamData);
      if (streamData.length > 0) {
        // Find first active stream, or fallback to any stream if provided selectedStreamId
        const initial = selectedStreamId 
          ? streamData.find(s => s.id === selectedStreamId) || streamData.find(s => s.is_active !== false) || streamData[0]
          : streamData.find(s => s.is_active !== false) || streamData[0];
        setSelectedStream(initial);
        onStreamChange?.({ id: initial.id, name: initial.name, description: initial.description, videoId: initial.videoId });
      }
      setIsLoading(false);
    };

    loadStreams();
  }, [toast]);

  // Load YouTube iframe API
  useEffect(() => {
    if (!(window as any).YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }

    // Wait for API to load
    (window as any).onYouTubeIframeAPIReady = () => {
      initPlayer();
    };

    if ((window as any).YT && (window as any).YT.Player) {
      initPlayer();
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
      }
    };
  }, []);

  const initPlayer = () => {
    if (!containerRef.current || !selectedStream) return;

    playerRef.current = new (window as any).YT.Player(playerElementId.current, {
      height: '1',
      width: '1',
      videoId: selectedStream.videoId,
        playerVars: {
          autoplay: 0,
          controls: 0,
          modestbranding: 1,
          playsinline: 1,
          rel: 0,
          showinfo: 0,
          origin: window.location.origin
        },
      events: {
        onReady: (event: any) => {
          event.target.unMute?.();
          event.target.setVolume(localVolume);
          const shouldPlay = autoPlay || (window as any).__ytRadioShouldPlay;
          if (shouldPlay) {
            (window as any).__ytRadioShouldPlay = false;
            event.target.playVideo();
          }
        },
        onStateChange: (event: any) => {
          const playing = event.data === (window as any).YT.PlayerState.PLAYING;
          setIsPlaying(playing);
          onPlayingChange?.(playing);
        }
      }
    });
  };

  const togglePlay = () => {
    // Ensure player exists; if not, initialize and try to play immediately
    if (!playerRef.current) {
      initPlayer();
      // Attempt to play after initialization
      setTimeout(() => {
        try {
          playerRef.current?.unMute?.();
          playerRef.current?.playVideo?.();
        } catch (e) {
          console.warn('Delayed play failed before player ready');
        }
      }, 0);
      return;
    }

    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  };

  const handleStreamChange = (stream: RadioStream) => {
    setSelectedStream(stream);
    onStreamChange?.({ id: stream.id, name: stream.name, description: stream.description, videoId: stream.videoId });
    if (playerRef.current && playerRef.current.loadVideoById) {
      playerRef.current.loadVideoById(stream.videoId);
      if (isPlaying || autoPlay) {
        playerRef.current.playVideo();
      }
    }
  };

  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setLocalVolume(newVolume);
    if (playerRef.current && playerRef.current.setVolume) {
      playerRef.current.setVolume(newVolume);
    }
    onVolumeChange?.(newVolume);
  };

  useEffect(() => {
    if (playerRef.current && playerRef.current.setVolume) {
      playerRef.current.setVolume(volume);
      setLocalVolume(volume);
    }
  }, [volume]);

  // Auto-play effect - start/stop player when autoPlay prop changes
  useEffect(() => {
    if (!playerRef.current) return;
    
    if (autoPlay && !isPlaying) {
      console.log('Starting YouTube radio via autoPlay prop');
      playerRef.current.playVideo();
    } else if (!autoPlay && isPlaying) {
      console.log('Stopping YouTube radio via autoPlay prop');
      playerRef.current.pauseVideo();
    }
  }, [autoPlay]);

  // Initialize or switch player when selectedStream changes
  useEffect(() => {
    const YT = (window as any).YT;
    if (!selectedStream) return;

    if (YT && YT.Player) {
      if (!playerRef.current) {
        initPlayer();
      } else if (playerRef.current?.loadVideoById) {
        playerRef.current.loadVideoById(selectedStream.videoId);
        if (autoPlay || (window as any).__ytRadioShouldPlay) {
          (window as any).__ytRadioShouldPlay = false;
          playerRef.current.playVideo?.();
        }
      }
    }
  }, [selectedStream]);

  // Global play/pause and stream select events
  useEffect(() => {
    if (!listenToGlobalEvents) return;

    const onPlay = () => {
      if (playerRef.current?.playVideo) {
        playerRef.current.unMute?.();
        playerRef.current.playVideo();
      } else {
        (window as any).__ytRadioShouldPlay = true;
      }
    };
    const onPause = () => {
      playerRef.current?.pauseVideo?.();
    };
    const onSelect = (e: any) => {
      const id = e?.detail?.id as string | undefined;
      if (!id) return;
      const target = streams.find(s => s.id === id && s.is_active !== false) || streams.find(s => s.id === id);
      if (target) {
        handleStreamChange(target);
      }
    };

    window.addEventListener('yt-radio-play', onPlay);
    window.addEventListener('yt-radio-pause', onPause);
    window.addEventListener('yt-radio-select-stream', onSelect as EventListener);
    return () => {
      window.removeEventListener('yt-radio-play', onPlay);
      window.removeEventListener('yt-radio-pause', onPause);
      window.removeEventListener('yt-radio-select-stream', onSelect as EventListener);
    };
  }, [streams, listenToGlobalEvents]);

  if (isLoading) {
    return (
      <Card className="glass-card p-6 h-full flex items-center justify-center">
        <p className="text-muted-foreground">Loading streams...</p>
      </Card>
    );
  }

  if (!selectedStream || streams.length === 0) {
    return (
      <Card className="glass-card p-6 h-full flex items-center justify-center">
        <p className="text-muted-foreground">No active streams available</p>
      </Card>
    );
  }

  return (
    <Card className="glass-card p-6 h-full">
      <div className="space-y-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Radio className="h-6 w-6 text-primary" />
            <h2 className="text-xl font-bold">YouTube Radio</h2>
          </div>
          <p className="text-sm text-muted-foreground">24/7 Lofi Hip Hop Streams</p>
        </div>

        {/* Hidden YouTube player */}
        <div ref={containerRef} className="absolute w-px h-px -m-px overflow-hidden opacity-0 pointer-events-none -z-10" aria-hidden>
          <div id={playerElementId.current}></div>
        </div>

        {/* Play/Pause Control */}
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="lg"
            onClick={togglePlay}
            className="h-16 w-16 rounded-full"
          >
            {isPlaying ? (
              <Pause className="h-6 w-6" />
            ) : (
              <Play className="h-6 w-6 ml-0.5" />
            )}
          </Button>
        </div>

        {/* Currently Playing */}
        <div className="text-center p-4 rounded-lg bg-background/50">
          <p className="text-xs text-muted-foreground mb-1">NOW PLAYING</p>
          <h3 className="font-semibold text-lg">{selectedStream.name}</h3>
          <p className="text-sm text-muted-foreground">{selectedStream.description}</p>
        </div>

        {/* Volume Control */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Radio Volume</label>
            <span className="text-xs text-muted-foreground">{localVolume}%</span>
          </div>
          <Slider
            value={[localVolume]}
            onValueChange={handleVolumeChange}
            max={100}
            step={1}
          />
        </div>

        {/* Stream Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Available Streams</label>
          <div className="space-y-2">
            {streams.map((stream) => (
              <div
                key={stream.id}
                onClick={() => handleStreamChange(stream)}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200",
                  "hover:bg-white/5",
                  selectedStream.id === stream.id && "bg-primary/10 border border-primary/20"
                )}
              >
                 <div className="flex items-center justify-center h-10 w-10 rounded-full bg-background/50 flex-shrink-0">
                  <Radio className={cn(
                    "h-4 w-4",
                    selectedStream.id === stream.id && "text-primary"
                  )} />
                  {/* Status indicator */}
                  <div className={cn(
                    "absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border border-background",
                    stream.is_active ? "bg-green-500" : "bg-red-500"
                  )} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className={cn(
                      "text-sm font-medium truncate",
                      selectedStream.id === stream.id && "text-primary"
                    )}>
                      {stream.name}
                    </h3>
                    {!stream.is_active && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/10 text-red-500 whitespace-nowrap">
                        Offline
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{stream.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};
